/**
 * BankTransferUpload — lets the member upload a bank transfer receipt.
 *
 * Validates file type (magic-byte check must pass server-side; here we do a
 * client-side extension/MIME pre-filter for UX), enforces the 10 MB limit
 * client-side, shows upload progress, and calls the upload endpoint.
 */
import { useRef, useState } from 'react'
import { Upload, CheckCircle, AlertCircle, Loader2, FileText } from 'lucide-react'
import { uploadBankTransferReceipt } from '@/services/paymentService'

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const ALLOWED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png'
const MAX_FILE_BYTES = 10 * 1024 * 1024 // 10 MB

interface BankTransferUploadProps {
  invoiceId: number
  /** HTML string with bank account details (IBAN, account name, etc.) configured in portal config. */
  bankTransferInfo?: string | null
  /** Called after a successful upload. */
  onSuccess?: (paymentId: number, documentId: number) => void
}

type UploadState = 'idle' | 'uploading' | 'done' | 'error'

export function BankTransferUpload({ invoiceId, bankTransferInfo, onSuccess }: BankTransferUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    setErrorMessage('')

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setSelectedFile(null)
      setUploadState('idle')
      setErrorMessage('Only PDF, JPEG, and PNG files are allowed.')
      return
    }

    if (file.size > MAX_FILE_BYTES) {
      setSelectedFile(null)
      setUploadState('idle')
      setErrorMessage('File size exceeds the 10 MB limit.')
      return
    }

    setSelectedFile(file)
    setUploadState('idle')
  }

  async function handleUpload() {
    if (!selectedFile) return

    setUploadState('uploading')
    setUploadProgress(0)
    setErrorMessage('')

    try {
      const result = await uploadBankTransferReceipt(invoiceId, selectedFile, setUploadProgress)
      setUploadState('done')
      onSuccess?.(result.payment_id, result.document_id)
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Upload failed. Please try again.'
      setErrorMessage(message)
      setUploadState('error')
    }
  }

  function handleDropZoneClick() {
    inputRef.current?.click()
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const file = event.dataTransfer.files?.[0]
    if (!file) return
    // Simulate input change
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    if (inputRef.current) {
      inputRef.current.files = dataTransfer.files
      inputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  if (uploadState === 'done') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-6 text-center">
        <CheckCircle className="h-10 w-10 text-green-500" />
        <p className="font-semibold text-green-700">Receipt uploaded successfully</p>
        <p className="text-sm text-green-600">
          Your payment is pending review. You'll be notified once it's approved.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Bank transfer info — institution-configured account details */}
      {/* TODO: sanitize bank_transfer_info server-side (e.g. bleach allowlist) before serving it
           via the branding endpoint, so this render is safe even if an admin account is compromised. */}
      {bankTransferInfo && (
        <div
          className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 [&_a]:underline [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: bankTransferInfo }}
        />
      )}

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleDropZoneClick}
        onKeyDown={(event) => event.key === 'Enter' && handleDropZoneClick()}
        onDrop={handleDrop}
        onDragOver={(event) => event.preventDefault()}
        className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center transition hover:border-blue-400 hover:bg-blue-50"
      >
        {selectedFile ? (
          <>
            <FileText className="h-8 w-8 text-blue-500" />
            <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
            <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(0)} KB</p>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-700">
              Tap to select or drag & drop your receipt
            </p>
            <p className="text-xs text-gray-500">PDF, JPEG, or PNG — max 10 MB</p>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_EXTENSIONS}
        className="sr-only"
        onChange={handleFileChange}
      />

      {/* Error message */}
      {errorMessage && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Upload progress */}
      {uploadState === 'uploading' && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Uploading…</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-blue-500 transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Submit button */}
      <button
        type="button"
        onClick={handleUpload}
        disabled={!selectedFile || uploadState === 'uploading'}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px]"
      >
        {uploadState === 'uploading' ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading…
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            Upload Receipt
          </>
        )}
      </button>
    </div>
  )
}
